<script>
import { nanoid } from 'nanoid'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import { RateUnitType } from '@/shared/constants/booking/RateUnitType'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'
import { isMonthBeforeDay } from '@/helpers/date-helpers'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { TimesheetMethod } from '@/shared/constants/timesheet/TimesheetMethod'
import TimesheetCandidateDisputeStatusChip from './timesheet-candidate-dispute-status-chip.vue'

export default {
  name: 'TimeSheetCandidateCard',
  components: { CandidateImage, TimesheetCandidateDisputeStatusChip },
  props: {
    candidateTimeSheets: {
      type: Array,
      required: true,
    },
    selectAll: {
      type: Boolean,
    },
  },
  data: () => {
    return {
      key: '',
      headers: [
        {
          text: 'Date',
          align: 'start',
          value: 'startTimeUTC',
          width: '15%',
          sort: (a, b) => a.toDate() - b.toDate(),
        },
        {
          text: 'Location',
          align: 'start',
          value: 'locationName',
          width: '25%',
        },
        {
          text: 'Hours',
          align: 'start',
          value: 'hours',
          width: '16%',
        },
        {
          text: 'Actions',
          align: 'start',
          value: 'actions',
          width: '34%',
        },
        {
          text: 'ID',
          align: 'end',
          value: 'bookingId',
          width: '5%',
        },
      ],
      selectedTimesheets: [],
      TimesheetOperationType,
    }
  },
  watch: {
    selectAll: {
      handler() {
        if (this.selectAll) {
          this.selectedTimesheets = this.candidateTimeSheets
        } else {
          this.selectedTimesheets = []
        }
      },
    },
    selectedTimesheets: {
      deep: true,
      handler() {
        this.emitSelectedTable()
      },
    },
  },
  created() {
    this.key = nanoid()
    this.emitSelectedTable() // initiate top level's mgnt of the selection
  },
  methods: {
    renderDate(dateUtc, timezone, format) {
      let formatString = format

      if (!formatString) {
        formatString = isMonthBeforeDay(this.$i18n.locale.toLowerCase())
          ? DateFormatToken.dddMMDDYYYYWithSlash
          : DateFormatToken.dddDDMMYYYYWithSlash
      }

      const dateTz = dateUtc.tz(timezone)
      return dateTz.format(formatString)
    },
    renderTime(dateUtc, timezone) {
      return this.renderDate(dateUtc, timezone, 'HH:mm')
    },
    changeTime(timesheet) {
      this.$emit('changeTime', {
        key: this.key,
        timesheet: timesheet,
      })
    },
    cancelBooking(timesheet) {
      timesheet.operationType = TimesheetOperationType.CANCEL

      const index = this.selectedTimesheets.findIndex(
        (x) => x.bookingId === timesheet.bookingId
      )

      if (index < 0) {
        this.selectedTimesheets.push(timesheet)
      }
    },
    undoChanges(timesheet) {
      timesheet.newStartTimeLocal = null
      timesheet.newEndTimeLocal = null
      timesheet.newPayOptionType = null
      timesheet.newBreakMinutes = null
      timesheet.operationType = TimesheetOperationType.ACCEPT

      const index = this.selectedTimesheets.findIndex(
        (x) => x.bookingId === timesheet.bookingId
      )

      if (index >= 0) {
        this.selectedTimesheets.splice(index, 1)
      }
    },
    rowClass(item) {
      return item.operationType === TimesheetOperationType.CANCEL
        ? 'red lighten-5'
        : item.operationType === TimesheetOperationType.MODIFY
        ? 'amber lighten-5'
        : ''
    },
    emitSelectedTable() {
      this.$emit('timesheetsUpdated', {
        key: this.key,
        timesheets: this.selectedTimesheets,
      })
    },
    isHourlyUnitType(timesheet) {
      return timesheet?.unitType === RateUnitType.HOURLY
    },
    getBreakMinutesText(minutes) {
      return minutesToHoursMinsString(minutes)
    },
    isCandidateTimesheeting(timesheet) {
      return timesheet?.timesheetMethod === TimesheetMethod.candidate
    },
    showBreaks(timesheet) {
      return (
        this.isHourlyUnitType(timesheet) ||
        this.isCandidateTimesheeting(timesheet)
      )
    },
  },
}
</script>

<template>
  <v-card class="cl-elevation rounded-xl">
    <v-card-title class="d-flex align-center">
      <v-lazy>
        <CandidateImage
          :id="candidateTimeSheets[0].candidateId"
          :first-name="candidateTimeSheets[0].candidateFirstName"
          :last-name="candidateTimeSheets[0].candidateLastName"
          :size="'lg'"
        />
      </v-lazy>
      <p class="ml-3 my-0 subtitle-2">
        {{ candidateTimeSheets[0].candidateFullName }}
      </p>
    </v-card-title>
    <v-data-table
      v-model="selectedTimesheets"
      :headers="headers"
      :items="candidateTimeSheets"
      :item-class="rowClass"
      :sort-by="['startTimeUTC']"
      :sort-desc="[false]"
      item-key="bookingId"
      disable-pagination
      hide-default-footer
      show-select
      class="elevation-0 rounded-xl"
    >
      <template v-slot:[`item.startTimeUTC`]="{ item }">
        {{ renderDate(item.startTimeUTC, item.locationTimeZone) }}
      </template>

      <template v-slot:[`item.hours`]="{ item }">
        {{ renderTime(item.startTimeUTC, item.locationTimeZone) }}
        -
        {{ renderTime(item.endTimeUTC, item.locationTimeZone) }}
        {{ item.payOptionType }}
        <div v-if="showBreaks(item)" class="d-inline-block ml-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                data-testid="timesheet-candidate-card-break-minutes"
                v-on="on"
              >
                {{ getBreakMinutesText(item.breakMinutes) }}
              </span>
            </template>
            <span>
              {{ $t('timesheets.breaksLabel') }}
            </span>
          </v-tooltip>
        </div>
        <TimesheetCandidateDisputeStatusChip :timesheet="item" />
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div v-if="item.operationType === TimesheetOperationType.ACCEPT">
          <v-btn
            x-small
            text
            color="blue"
            title="Update the times for this booking"
            @click="changeTime(item)"
          >
            <v-icon small class="mr-1">mdi-pencil</v-icon>
            {{ $t('timesheets.timesheetModifyButtonText') }}
          </v-btn>
          <v-btn
            x-small
            text
            color="red"
            title="Cancel this booking"
            @click="cancelBooking(item)"
          >
            <v-icon small class="mr-1">mdi-cancel</v-icon>
            {{ $t('timesheets.timesheetCancelButtonText') }}
          </v-btn>
        </div>
        <div v-else>
          <v-chip x-small class="ma-0" style="height:auto; white-space:normal;">
            <span>
              {{
                item.operationType === TimesheetOperationType.MODIFY
                  ? $t('timesheets.flagAsPendingReviewText')
                  : $t('timesheets.flagAsCancelledText')
              }}
            </span>
          </v-chip>
          <div>
            <span v-if="item.operationType === TimesheetOperationType.MODIFY">
              Changing to {{ item.newStartTimeLocal }} -
              {{ item.newEndTimeLocal }}
              {{
                showBreaks(item)
                  ? `( ${getBreakMinutesText(item.newBreakMinutes)} break )`
                  : ''
              }}
              {{ item.newPayOptionType }}
            </span>
            <v-btn
              x-small
              text
              color="blue"
              title="Undo Changes"
              @click="undoChanges(item)"
            >
              <v-icon small class="mr-1">mdi-undo</v-icon>
              {{ $t('timesheets.timesheetUndoButtonText') }}
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:[`item.bookingId`]="{ item }">
        <span class="font-monospace">#{{ item.bookingId }}</span>
      </template>
    </v-data-table>
  </v-card>
</template>
